import React, { useState, useEffect, useRef, useCallback } from "react";
import { UTILS } from "../../config/API";
import RadioField from "../RadioField";
import CheckboxField from "../CheckboxField";

const StampInvoices = ({ initialData = {}, onDataChange }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState({});
    const [error, setError] = useState(null);
    const [picklistCache, setPicklistCache] = useState({});
    const [sectionGroups, setSectionGroups] = useState([{
        id: 1,
        exemptionGroups: [1],
        splitpayment: { fields: [], title: "Split Payment Information", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" },
        exemption: { fields: [], title: "Exemption Information", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" }
    }]);
    const [taxPayerName, setTaxPayerName] = useState("");
    const timeoutRef = useRef(null);

    useEffect(() => {
        const selectedTaxPayer = localStorage.getItem("selectedBusinessName");
        if (selectedTaxPayer) {
            setTaxPayerName(selectedTaxPayer);
        }
    }, []);

    // Initialize form data with initialData
    useEffect(() => {
        if (Object.keys(initialData).length > 0) {
            setFormData(prevData => ({
                ...prevData,
                ...initialData
            }));
        }
    }, [initialData]);

    // Debounced data change effect
    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            if (onDataChange && typeof onDataChange === 'function') {
                const formattedData = formatStampInvoicesData(formData, sectionGroups);
                onDataChange(formattedData);
            }
        }, 300);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [formData, sectionGroups]);

    const formatStampInvoicesData = useCallback((formData, sectionGroups) => {
        const formattedData = {
            stampInvoices: []
        };

        sectionGroups.forEach(group => {
            const invoiceGroup = {};

            // Process split payment fields
            if (group.splitpayment && group.splitpayment.fields) {
                group.splitpayment.fields.forEach(field => {
                    // Check if field.fieldname exists and is not empty
                    if (field.fieldname && field.fieldname.trim() !== '') {
                        const fieldKey = `${field.fieldname}_${group.id}`;
                        if (formData[fieldKey] !== undefined && formData[fieldKey] !== '') {
                            invoiceGroup[field.fieldname] = formData[fieldKey];
                        }
                    }
                });
            }

            formattedData.stampInvoices.push(invoiceGroup);
        });

        return formattedData;
    }, []);

    useEffect(() => {
        const fetchFormFields = async (itype, stype) => {
            setIsLoading(true);
            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({
                        action: "desc",
                        itype: itype,
                        stype: stype,
                    }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
                console.log(cleanedText);

                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse form fields:", parseError);
                    throw new Error("Invalid server response format");
                }

                if (response.ok) {
                    return data;
                } else {
                    throw new Error(data.message || "Failed to fetch form fields");
                }
            } catch (error) {
                console.log(itype)
                console.log(stype)
                console.error("Error loading form:", error);
                setError(error.message);
                return null;
            }
        };

        const fetchDropDownFields = async (itemValueGroup) => {
            if (picklistCache[itemValueGroup]?.data) return;

            setPicklistCache((prev) => ({
                ...prev,
                [itemValueGroup]: { ...prev[itemValueGroup], isLoading: true },
            }));

            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({ action: "idv", s: itemValueGroup || "YesNo" }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse dropdown data:", parseError);
                    console.error("Original text:", text);
                    throw new Error("Invalid dropdown data format");
                }

                setPicklistCache((prev) => ({
                    ...prev,
                    [itemValueGroup]: { data, isLoading: false },
                }));
            } catch (error) {
                console.error("Error fetching dropdown:", error);
                setPicklistCache((prev) => ({
                    ...prev,
                    [itemValueGroup]: { isLoading: false, error: error.message },
                }));
            }
        };

        const fetchAndCombineData = async () => {
            try {
                const splitPaymentData = await fetchFormFields("document", "taxinvoice");

                if (!splitPaymentData) return;

                setSectionGroups([{
                    id: 1,
                    exemptionGroups: [1],
                    splitpayment: {
                        fields: splitPaymentData.Fields || [],
                        signature: splitPaymentData.Signature,
                        signatureText: splitPaymentData.SignatureText || "",
                        fileUpload: splitPaymentData.FileUpload,
                        fileUploadText: splitPaymentData.FileUploadText || "",
                        fileTypes: splitPaymentData.FileTypes || ""
                    },
                }]);

                const allFields = [
                    ...(splitPaymentData.Fields || []),
                ];

                // Initialize form data with proper field naming
                const initialFormData = {};
                allFields.forEach((field) => {
                    const fieldKey = `${field.fieldname}_1`; // Default to group 1
                    if (field.defaultvalue) {
                        initialFormData[fieldKey] = field.defaultvalue;
                    } else {
                        // Initialize with empty string to ensure controlled inputs
                        initialFormData[fieldKey] = "";
                    }
                });

                setFormData(prevData => ({
                    ...prevData,
                    ...initialFormData
                }));

                const picklistFields = allFields.filter(
                    (field) => field.fieldtype === "picklist"
                );
                await Promise.all(
                    picklistFields.map(async (field) => {
                        await fetchDropDownFields(field.itemvaluegroup);
                    })
                );
            } catch (error) {
                console.error("Error in fetchAndCombineData:", error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAndCombineData();
    }, []);

    const handleAddSectionGroup = () => {
        const newGroupId = sectionGroups.length > 0
            ? Math.max(...sectionGroups.map(g => g.id)) + 1
            : 1;

        if (!sectionGroups[0] || !sectionGroups[0].splitpayment) {
            console.error("Cannot add section group: missing required data structure");
            return;
        }

        setSectionGroups(prev => [
            ...prev,
            {
                id: newGroupId,
                exemptionGroups: [1],
                splitpayment: { ...prev[0].splitpayment },
                exemption: { ...prev[0].exemption }
            }
        ]);

        const initialData = {};

        const splitFields = sectionGroups[0]?.splitpayment?.fields || [];

        splitFields.forEach(field => {
            const fieldKey = `${field.fieldname}_${newGroupId}`;
            if (field && field.defaultvalue) {
                initialData[fieldKey] = field.defaultvalue;
            } else {
                initialData[fieldKey] = "";
            }
        });

        setFormData(prev => ({
            ...prev,
            ...initialData
        }));
    };

    const handleRemoveSectionGroup = (groupId) => {
        if (sectionGroups.length > 1) {
            setSectionGroups(prev => prev.filter(group => group.id !== groupId));
            setFormData(prev => {
                const newData = { ...prev };
                Object.keys(prev).forEach(key => {
                    if (key.endsWith(`_${groupId}`)) {
                        delete newData[key];
                    }
                });
                return newData;
            });
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prevFormData) => {
            let newValue;

            if (type === 'checkbox') {
                const currentValues = prevFormData[name] || [];
                newValue = checked
                    ? [...currentValues, value]
                    : currentValues.filter(item => item !== value);
            } else {
                newValue = value;
            }

            return { ...prevFormData, [name]: newValue };
        });
    };

    const handleCheckboxGroupChange = (e, fieldName) => {
        const { value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [fieldName]: checked
                ? [...(prev[fieldName] || []), value]
                : (prev[fieldName] || []).filter(item => item !== value)
        }));
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="relative">
                    <div className="w-16 h-16 rounded-full absolute border-4 border-gray-200"></div>
                    <div className="w-16 h-16 rounded-full animate-spin border-4 border-primary border-t-transparent border-r-transparent"></div>

                    <div className="absolute inset-0 flex items-center justify-center">
                        <svg
                            className="w-6 h-6 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            ></path>
                        </svg>
                    </div>
                </div>

                <div className="flex flex-col items-center space-y-2">
                    <p className="text-gray-600 font-medium text-lg animate-pulse">Preparing Your Form...</p>
                    <div className="flex space-x-2">
                        {[...Array(3)].map((_, i) => (
                            <div
                                key={i}
                                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                style={{ animationDelay: `${i * 0.1}s` }}
                            ></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Form</h2>
                <p className="text-gray-700 mb-4">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-[#711515] text-white px-6 py-2 rounded-lg hover:bg-[#5a1010] transition"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="w-[95%] md:w-[80%] p-4 md:p-8 mx-auto bg-white mt-5 rounded-lg">
            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                    <p>{error}</p>
                </div>
            )}

            {taxPayerName && (
                <p className="text-lg text-gray-600 -mt-6 text-center font-semibold">
                    For: <span className="text-lg  text-[#711515]">{taxPayerName}</span>
                </p>
            )}

            <div className="flex flex-col gap-8">
                {/* Section Groups (Split Payment + Exemption) */}
                {sectionGroups.map((group) => (
                    <div key={group.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-800">Line {group.id}</h3>
                            {group.id > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveSectionGroup(group.id)}
                                    className="text-red-500 hover:text-red-700 font-medium"
                                >
                                    Remove Line
                                </button>
                            )}
                        </div>

                        {/* Split Payment Information */}
                        <div className="mb-8">
                            <h4 className="text-lg font-bold text-gray-700 border-b border-gray-300 pb-2 mb-4">
                                {group.splitpayment.title}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {group.splitpayment.fields
                                    .filter(
                                        (field) =>
                                            ![
                                                "itemid",
                                                "autonumber",
                                                "readonlydate",
                                                "readonlypicklist",
                                                "readonly",
                                                "hidden",
                                                "readonlylookup",
                                                "linkedfield",
                                                "inlinequery",
                                                "person",
                                            ].includes(field.fieldtype)
                                    )
                                    .sort((a, b) => a.sortorder - b.sortorder)
                                    .map((field) => (
                                        <div className={`${field.fieldtype === "label" ? "col-span-2" : "col-span-1"}`} key={`split_${group.id}_${field.displayid}`}>
                                            <label className={`block ${field.fieldtype === "label" ? "font-bold text-base text-xl w-full" : "text-gray-700 font-semibold"} mb-3`}>
                                                {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                                {field.tooltip && (
                                                    <div className="inline-block relative ml-2 group">
                                                        <svg
                                                            className="w-5 h-5 text-gray-400 cursor-help"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                            />
                                                        </svg>
                                                        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                            {field.tooltip}
                                                            <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </label>

                                            {field.fieldtype === "date" ? (
                                                <input
                                                    type="date"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                />
                                            ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" || field.fieldtype === "textdate" ? (
                                                <input
                                                    type="text"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                    maxLength={field.maxlength || ""}
                                                />
                                            ) : field.fieldtype === "textarea" ? (
                                                <textarea
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                    rows="4"
                                                ></textarea>
                                            ) : field.fieldtype === "picklist" ? (
                                                <div className="relative">
                                                    <select
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        onChange={handleChange}
                                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                        required={field.required === "Y"}
                                                    >
                                                        <option value="">Select {field.displayname}</option>
                                                        {picklistCache[field.itemvaluegroup]?.data?.map((option) => (
                                                            <option key={option.valueid} value={option.valuetext}>
                                                                {option.valuetext}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                        <svg
                                                            className="h-4 w-4"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                            ) : field.fieldtype === "checkbox" ? (
                                                <CheckboxField
                                                    field={field}
                                                    formData={formData}
                                                    handleCheckboxGroupChange={(e) => {
                                                        const { value, checked } = e.target;
                                                        const fieldName = `${field.fieldname}_${group.id}`;
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            [fieldName]: checked
                                                                ? [...(prev[fieldName] || []), value]
                                                                : (prev[fieldName] || []).filter(item => item !== value)
                                                        }));
                                                    }}
                                                    picklistCache={picklistCache}
                                                    setPicklistCache={setPicklistCache}
                                                    groupId={group.id}
                                                />
                                            ) : field.fieldtype === "radio" ? (
                                                <RadioField
                                                    field={field}
                                                    formData={formData}
                                                    handleChange={handleChange}
                                                    picklistCache={picklistCache}
                                                    setPicklistCache={setPicklistCache}
                                                    groupId={group.id}
                                                />
                                            ) : null}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                ))}

                {/* Add Section Group Button */}
                <button
                    type="button"
                    onClick={handleAddSectionGroup}
                    className="flex items-center justify-center bg-[#711515] text-white px-6 py-3 rounded-lg hover:bg-[#5a1010] transition"
                >
                    <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Another Line
                </button>
            </div>
        </div>
    );
};

export default StampInvoices;